import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  inject,
  signal,
  WritableSignal
} from '@angular/core';
import { Router } from '@angular/router';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { AuthActionType } from '../../auth.type';
import { NotificationService } from '@Shared/services';
import { Subject } from 'rxjs';
import { BusinessService, UserCommonApiService } from '@App/common/services';
import { AuthService } from '@App/pages/auth/services';
import { takeUntil } from 'rxjs/operators';
import { CustomValidators, equalValueValidator } from '@App/shared/utils';
import { OtpRequestModel, ResetPasswordModel } from '@App/common/models';
import { CommonModule, NgClass, NgIf } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TranslateModule } from '@ngx-translate/core';
import { NoLeadingSpaceDirective } from '@App/common/directives';
import { KeyFilter } from 'primeng/keyfilter';

@Component({
  selector: 'app-reset-password',
  styleUrls: ['./reset-password.component.scss'],
  templateUrl: './reset-password.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    TranslateModule,
    NgClass,
    NgIf,
    NoLeadingSpaceDirective,
    KeyFilter
  ],
  encapsulation: ViewEncapsulation.Emulated
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  @Input() profileVerification = false;
  @Output() activeTypeChange: EventEmitter<AuthActionType> = new EventEmitter();
  @ViewChild('newpasswordInput')
  newpasswordInput?: ElementRef<HTMLInputElement>;
  @ViewChild('confirmpasswordInput')
  confirmpasswordInput?: ElementRef<HTMLInputElement>;

  resetPasswordForm: UntypedFormGroup;
  contactNumber: string = '';
  emailId: string = '';
  matchError = true; // kept for API compatibility where used synchronously
  submitted = false;
  loading = false;
  errorMessage: any;
  destroy$ = new Subject();
  // otpTimer/isTimerRunning/isOtpEnabled/showPasswordRequirements moved to signals
  private isOtpEnabledSig: WritableSignal<boolean> = signal(false);
  private isTimerRunningSig: WritableSignal<boolean> = signal(true);
  private showPasswordRequirementsSig: WritableSignal<boolean> = signal(false);
  private otpTimerSig: WritableSignal<string> = signal('00');
  private matchErrorSig: WritableSignal<boolean> = signal(true);
  private token: number;
  private passwordVisibility: Record<
    'newpassword' | 'confirmpassword',
    boolean
  > = {
    newpassword: false,
    confirmpassword: false
  };
  // inject services
  private formBuilder = inject(UntypedFormBuilder);
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private businessService = inject(BusinessService);
  private userCommonService = inject(UserCommonApiService);
  private authService = inject(AuthService);

  constructor() {
    this.startTimer();
    this.userCommonService
      .isOtpEnabled()
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => {
        this.isOtpEnabledSig.set(enabled);
      });
  }

  // expose template-compatible properties
  get otpTimer(): string {
    return this.otpTimerSig();
  }

  get isTimerRunning(): boolean {
    return this.isTimerRunningSig();
  }

  get showPasswordRequirements(): boolean {
    return this.showPasswordRequirementsSig();
  }

  set showPasswordRequirements(v: boolean) {
    this.showPasswordRequirementsSig.set(!!v);
  }

  get f() {
    return this.resetPasswordForm.controls;
  }

  isPasswordVisible(field: 'newpassword' | 'confirmpassword'): boolean {
    return !!this.passwordVisibility[field];
  }

  toggleNewPassword(): void {
    this.passwordVisibility.newpassword = !this.passwordVisibility.newpassword;
    const el = this.newpasswordInput?.nativeElement;
    if (el) {
      el.type = this.passwordVisibility.newpassword ? 'text' : 'password';
    }
  }

  toggleConfirmPassword(): void {
    this.passwordVisibility.confirmpassword =
      !this.passwordVisibility.confirmpassword;
    const el = this.confirmpasswordInput?.nativeElement;
    if (el) {
      el.type = this.passwordVisibility.confirmpassword ? 'text' : 'password';
    }
  }

  ngOnInit() {
    this.getActionParams();
    this.contactNumber = this.isOtpEnabledSig()
      ? this.userCommonService.userContactNumber
      : this.userCommonService.userEmailId;
    this.resetPasswordForm = this.setFormData();
  }

  setFormData() {
    return this.formBuilder.group(
      {
        smsOtpCode: [this.token || '', [Validators.required]],
        newpassword: [
          '',
          [
            Validators.required,
            CustomValidators.patternValidator(/\d/, {
              hasNumber: true
            }),
            CustomValidators.patternValidator(/[A-Z]/, {
              hasCapitalCase: true
            }),
            CustomValidators.patternValidator(/[a-z]/, {
              hasSmallCase: true
            }),
            Validators.minLength(8)
          ]
        ],
        confirmpassword: [
          '',
          [
            Validators.required,
            CustomValidators.patternValidator(/\d/, {
              hasNumber: true
            }),
            CustomValidators.patternValidator(/[A-Z]/, {
              hasCapitalCase: true
            }),
            CustomValidators.patternValidator(/[a-z]/, {
              hasSmallCase: true
            }),
            Validators.minLength(8)
          ]
        ]
      },
      { validator: equalValueValidator('newpassword', 'confirmpassword') }
    );
  }

  getEmailIdFromUrl() {
    this.emailId = decodeURIComponent(
      this.router.parseUrl(this.router.url)?.queryParams?.emailId
    );
  }

  getFormData() {
    const password = this.resetPasswordForm.controls.newpassword.value.trim();
    const confirmPassword =
      this.resetPasswordForm.controls.confirmpassword.value.trim();
    const smsOtpCode = this.resetPasswordForm.controls.smsOtpCode.value.trim();

    const resetPasswordModel = new ResetPasswordModel();
    resetPasswordModel.password = password;
    resetPasswordModel.confirmPassword = confirmPassword;
    resetPasswordModel.emailId =
      (this.businessService.userEmailId as string) || (this.emailId as string);
    resetPasswordModel.smsOtpCode = smsOtpCode;
    return resetPasswordModel;
  }

  resetPassword() {
    if (!this.resetPasswordForm.invalid) {
      const resetPasswordModel = this.getFormData();
      if (this.matchError) {
        const subscription = this.isOtpEnabledSig()
          ? this.userCommonService.resetPassword(resetPasswordModel)
          : this.userCommonService.resetPasswordEmail(resetPasswordModel);
        subscription.subscribe({
          next: (result) => {
            this.notificationService.showNotification(
              'SUCCESS',
              result.message
            );
            this.doSignOut();
            this.activeTypeChange.emit('SIGN_IN');
            this.authService.setAction(null);
          },
          error: (error) => {
            this.errorMessage = error.Message;
          }
        });
      }
    }
  }

  resendOTP() {
    if (!this.isTimerRunningSig()) {
      this.errorMessage = null;
      this.isTimerRunningSig.set(true);
      const params = new OtpRequestModel();
      params.emailId = this.businessService.userEmailId || this.emailId;
      this.userCommonService.forgotPassword(params).subscribe({
        next: () => {
          this.startTimer();
        },
        error: () => {
          this.isTimerRunningSig.set(false);
        }
      });
    }
  }

  startTimer() {
    let timerValue = 30;
    const otpTimer = setInterval(() => {
      if (timerValue) {
        this.otpTimerSig.set(
          timerValue < 10 ? '0' + timerValue : '' + timerValue
        );
        --timerValue;
      } else {
        this.otpTimerSig.set('00');
        this.isTimerRunningSig.set(false);
        clearInterval(otpTimer);
      }
    }, 1000);
  }

  doSignOut() {
    if (this.userCommonService.isAuthenticated()) {
      this.userCommonService.logout().subscribe({
        next: () => {
          this.userCommonService.clearSession();
          this.router.navigate(['']);
        },
        error: () => {
          this.userCommonService.clearSession();
        }
      });
    } else {
      this.router.navigate(['']);
    }
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
    this.resetForm();
  }

  resetForm(): void {
    this.resetPasswordForm?.reset();
    this.resetPasswordForm?.markAsPristine();
    this.resetPasswordForm?.markAsUntouched();
    this.submitted = false;
    this.errorMessage = null;
  }

  private getActionParams() {
    this.authService.actionParams$
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        if (params) {
          this.emailId = params.emailId;
          this.token = params.token;
          this.resetPasswordForm.patchValue({
            smsOtpCode: this.token
          });
        } else {
          this.emailId = '';
          this.token = null;
        }
      });
  }
}
